import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonMatchPastComponent } from '@/app/components/shared/skeleton/skeleton-match-past/skeleton-match-past.component';
import { TranslationService } from '@/app/services/translation.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { H2hTabBarComponent } from '../../components/h2h-tab-bar/h2h-tab-bar.component';
import {
  IPastMatch,
  PastMatchItemComponent,
} from '../../components/past-match-item/past-match-item.component';
import { EPastType, Match } from '../../models';
import { MatchPastService, MatchSummaryService } from '../../services';

@Component({
  selector: 'app-h2h-new',
  standalone: true,
  imports: [
    TranslateModule,
    H2hTabBarComponent,
    PastMatchItemComponent,
    NoResultComponent,
    SkeletonMatchPastComponent,
  ],
  providers: [MatchPastService],
  templateUrl: './h2h.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class H2hComponent {
  private route = inject(ActivatedRoute);
  private translationService = inject(TranslationService);
  private matchPastService = inject(MatchPastService);
  private translateService = inject(TranslateService);

  defautlImg = environment.urlIMG + 'default.png';

  tabSelect = signal<EPastType>(EPastType.H2H);
  matchSummaryService = inject(MatchSummaryService);

  matchSummary = computed(() => {
    return this.matchSummaryService.matchSummary();
  });

  tsExternalMatchId = input.required<string>();
  sport: string = 'Futbol';

  matchId = signal<string | null>(null);

  page = signal<number>(1);

  matches = signal<IPastMatch[]>([]);

  urlIMG = environment.urlIMG;

  data = computed(() => ({
    ...this.matchPastService.past(),
    Matches: this.mapperMatchData(this.matchPastService.past()?.Matches || []),
  }));

  fetchStatus = computed(() => ({
    isLoading: this.matchPastService.isLoadingPast(),
    isError: this.matchPastService.isErrorPast(),
  }));

  constructor() {
    const matchId = this.route.snapshot.paramMap.get('matchId');
    if (matchId) {
      this.matchId.set(matchId);
      this.matchPastService.getMatchPast(matchId, this.tabSelect());
    }
  }

  getHomeTeamName() {
    return this.translationService.getTraduccionName(
      this.matchSummary()?.HomeTeam?.NameTranslation ?? null,
      this.matchSummary()?.HomeTeam?.Name || 'NA'
    );
  }

  getAwayTeamName() {
    return this.translationService.getTraduccionName(
      this.matchSummary()?.AwayTeam?.NameTranslation ?? null,
      this.matchSummary()?.AwayTeam?.Name || 'NA'
    );
  }

  handlePagination() {
    const nextPage = this.page() + 1;
    this.page.set(nextPage);

    this.matchPastService.getMatchPast(
      this.matchId()!,
      this.tabSelect(),
      nextPage
    );
  }

  handleTabChange(tab: EPastType) {
    if (tab === this.tabSelect()) return;

    this.matchPastService.past.set(null);

    this.tabSelect.set(tab);
    this.page.set(1);

    this.matchPastService.resetMatches();
    this.matchPastService.getMatchPast(this.matchId()!, tab, 1, true);
  }

  getDateFormatted(date: string) {
    return format(new Date(date), 'dd.MM.yyyy');
  }

  mapperMatchData(matchData: Match[]): IPastMatch[] {
    if (!matchData) return [];
    return matchData.map((match) => {
      return {
        away: {
          name: this.translationService.getTraduccionName(
            match.AwayNameTranslation,
            match.AwayName
          ),
          logo: match.AwayLogo ? match.AwayLogo : this.defautlImg,
          score: match.AwayScore,
          penaltyScore: match.AwayPenaltyScore,
        },
        home: {
          name: this.translationService.getTraduccionName(
            match.HomeNameTranslation,
            match.HomeName
          ),
          logo: match.HomeLogo ? match.HomeLogo : this.defautlImg,
          score: match.HomeScore,
          penaltyScore: match.HomePenaltyScore,
        },
        competition: {
          name: this.translationService.getTraduccionName(
            match.CompetitionNameTranslation,
            match.CompetitionName
          ),
          logo: match.CompetitionLogo ? match.CompetitionLogo : this.defautlImg,
        },
        id: match.Id,
        tsExternalId: match.TsExternalId ? match.TsExternalId : match.Id,
        matchDate: match.MatchDate
          ? this.getDateFormatted(match.MatchDate)
          : '',
        result: match.Result,
      };
    });
  }

  getTextLabel() {
    switch (this.tabSelect()) {
      case EPastType.H2H:
        return this.translateService.instant('ClashesWithEachOther');
      case EPastType.HOME:
        return `${this.translateService.instant(
          'LastMatches'
        )}: ${this.getHomeTeamName()}`;
      case EPastType.AWAY:
        return `${this.translateService.instant(
          'LastMatches'
        )}: ${this.getAwayTeamName()}`;
      default:
        return this.translateService.instant('ClashesWithEachOther');
    }
  }
}
